import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CertificateStatusEnum, IUserCertificate } from '@share/common-types';
import { getUserCertificatesAsync } from '@share/api';
import { AppThunk } from '@share/utils';

export interface ICertificatesState {
  data: IUserCertificate[];
  loading: boolean;
  error: string;
}

interface IGetUserCertificatesParams {
  email?: string;
  organizationId?: number;
  statuses?: CertificateStatusEnum[];
}

const initialState: ICertificatesState = {
  data: [],
  loading: false,
  error: '',
};

const certificatesSlice = createSlice({
  name: 'certificates',
  initialState,
  reducers: {
    setCertificates: (
      state: ICertificatesState,
      { payload }: PayloadAction<IUserCertificate[]>,
    ) => {
      state.data = payload;
    },
    setCertificatesLoading: (state: ICertificatesState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setCertificatesError: (state: ICertificatesState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    resetCertificates: () => {
      return initialState;
    },
  },
});

export const certificatesActions = certificatesSlice.actions;
export const certificatesReducer = certificatesSlice.reducer;

export const getUserCertificates = (params: IGetUserCertificatesParams): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(certificatesActions.setCertificatesLoading(true));
      dispatch(certificatesActions.setCertificatesError(''));

      const { data } = await getUserCertificatesAsync({
        email: params.email,
        organizationId: params.organizationId,
        statuses: params.statuses,
        paging: { pageNumber: 1, pageSize: 100 },
      });
      dispatch(certificatesActions.setCertificates(data?.items ?? []));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(certificatesActions.setCertificatesError(message));
      dispatch(certificatesActions.setCertificates([]));
    } finally {
      dispatch(certificatesActions.setCertificatesLoading(false));
    }
  };
};
